import type { MarketingInspirationItem } from './types';
import type {
  MarketingSocialCard,
  MarketingSocialDraft,
  MarketingSocialFormat,
} from './marketingWorkspaceState';
import { createMarketingId } from './marketingWorkspaceState';

const compact = (value: string | undefined, maxLength = 220): string => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const makeCard = (
  text: string,
  role: MarketingSocialCard['role'],
  sourceItemId?: string,
  exactQuote = false,
  exactSource = false
): MarketingSocialCard => ({
  id: createMarketingId('card'),
  // Verified manuscript excerpts and quotes remain verbatim. Only generated
  // or descriptive source copy may be shortened for card readability.
  text: exactQuote || exactSource ? String(text || '').trim() : compact(text),
  role,
  sourceItemId,
  exactQuote,
  exactSource,
});

export const splitExactPassage = (passage: string, maximumCards = 8): string[] => {
  let remaining = String(passage || '').replace(/\r\n?/g, '\n').trim();
  if (!remaining) return [];
  const chunks: string[] = [];
  while (remaining && chunks.length < maximumCards) {
    const cardsLeft = maximumCards - chunks.length;
    if (cardsLeft === 1) {
      chunks.push(remaining.trim());
      break;
    }
    if (remaining.length <= 320) {
      const paragraphBreak = remaining.indexOf('\n\n');
      if (paragraphBreak > 0) {
        chunks.push(remaining.slice(0, paragraphBreak).trim());
        remaining = remaining.slice(paragraphBreak).trimStart();
        continue;
      }
      chunks.push(remaining.trim());
      break;
    }
    const target = Math.max(180, Math.min(360, Math.ceil(remaining.length / cardsLeft)));
    const minimum = Math.max(80, Math.floor(target * 0.58));
    const search = remaining.slice(0, Math.min(remaining.length, target + 100));
    const boundaries = [
      search.lastIndexOf('\n\n', target + 80),
      ...Array.from(search.matchAll(/[.!?…。！？][”’"'»)]?(?:\s+|$)/g))
        .map(match => (match.index || 0) + match[0].length)
        .filter(index => index >= minimum && index <= target + 80),
      search.lastIndexOf(' ', target + 40),
    ].filter(index => index >= minimum);
    const cut = boundaries.length ? Math.max(...boundaries) : Math.min(target, remaining.length);
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trimStart();
  }
  return chunks.filter(Boolean);
};

const sourceCards = (items: MarketingInspirationItem[]): MarketingSocialCard[] => {
  const cards: MarketingSocialCard[] = [];
  items.forEach(item => {
    if (item.type === 'quote' && item.quoteText) {
      cards.push(makeCard(item.quoteText, 'quote', item.id, true));
      return;
    }
    if (item.type === 'scene') {
      if (item.sourceVerified && item.sourceExcerpt) {
        splitExactPassage(item.sourceExcerpt).forEach((text, index) => {
          cards.push(makeCard(text, index === 0 ? 'hook' : 'story', item.id, false, true));
        });
        return;
      }
      cards.push(makeCard(item.title || item.text, 'story', item.id));
      const beat = item.sceneVideo?.beats?.find(Boolean);
      if (beat) cards.push(makeCard(beat, 'story', item.id));
      else if (item.text && compact(item.text) !== compact(item.title)) {
        cards.push(makeCard(item.text, 'story', item.id));
      }
      return;
    }
    const mood = item.vibeTags?.length
      ? item.vibeTags.join(' · ')
      : item.text || item.title;
    cards.push(makeCard(mood, 'mood', item.id));
  });
  return cards;
};

export const createEmptySocialDraft = (
  format: MarketingSocialFormat,
  editionLanguage: string,
  bookTitle: string
): MarketingSocialDraft => ({
  format,
  editionLanguage,
  title: `${bookTitle || 'Book'} ${format === 'story_slides' ? 'story slides' : format === 'carousel' ? 'carousel' : 'quote card'}`,
  selectedSourceIds: [],
  tone: 'Cinematic and intriguing',
  callToAction: `Read ${bookTitle || 'the book'}`,
  spoilerLevel: 'low',
  campaignNote: '',
  caption: '',
  cards: [],
  updatedAt: new Date().toISOString(),
});

export const composeSocialCards = (
  draft: MarketingSocialDraft,
  allItems: MarketingInspirationItem[],
  bookTitle: string
): MarketingSocialCard[] => {
  const selected = draft.selectedSourceIds
    .map(id => allItems.find(item => item.id === id))
    .filter((item): item is MarketingInspirationItem => Boolean(item))
    .filter(item => {
      const levels = { low: 0, medium: 1, high: 2 };
      const itemLevel = String(item.spoilerLevel || 'low').toLowerCase() as keyof typeof levels;
      return (levels[itemLevel] ?? 0) <= levels[draft.spoilerLevel];
    });
  const selectedCards = sourceCards(selected);
  const first = selected[0];

  if (draft.format === 'quote_card') {
    const quote = selected.find(item => item.type === 'quote' && item.quoteText);
    const text = quote?.quoteText || first?.quoteText || first?.text || first?.title || `Discover ${bookTitle}`;
    return [makeCard(text, quote ? 'quote' : 'hook', quote?.id || first?.id, Boolean(quote?.quoteText))];
  }

  const verifiedScene = selected.find(
    item => item.type === 'scene' && item.sourceVerified && String(item.sourceExcerpt || '').trim()
  );
  if (verifiedScene) {
    const exactCards = splitExactPassage(
      verifiedScene.sourceExcerpt || '',
      draft.format === 'story_slides' ? 8 : 6
    ).map((text, index) => makeCard(
      text,
      index === 0 ? 'hook' : 'story',
      verifiedScene.id,
      false,
      true
    ));
    return [
      ...exactCards,
      makeCard(draft.callToAction || `Read ${bookTitle || 'the book'}`, 'cta'),
    ];
  }

  const cards: MarketingSocialCard[] = [
    makeCard(first?.title || `${draft.tone}: a story you will not forget`, 'hook', first?.id),
    ...selectedCards,
  ];
  const maximumBeforeCta = draft.format === 'story_slides' ? 9 : 7;
  return [
    ...cards.slice(0, maximumBeforeCta),
    makeCard(draft.callToAction || `Read ${bookTitle || 'the book'}`, 'cta'),
  ];
};

export const serializeSocialAsset = (draft: MarketingSocialDraft): string => {
  const body = draft.cards.map((card, index) => (
    `${index + 1}. ${card.exactQuote ? '[Exact quote] ' : card.exactSource ? '[Exact passage] ' : ''}${card.text}`
  )).join('\n\n');
  return `${draft.title}\n${draft.format.replace('_', ' ')} · ${draft.editionLanguage}\n\n${body}${draft.caption ? `\n\nCaption\n${draft.caption}` : ''}${draft.campaignNote ? `\n\nCampaign note\n${draft.campaignNote}` : ''}`;
};
